'use client';
import { Spinner } from '@/components/ui';
import { useUserContext } from '@/contexts/User';
import { useWalletConnection } from '@/hooks';
import { disconnectHandler } from '@/utils';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useAccount, useDisconnect } from 'wagmi';

const UserAuthorize = ({ children }: { children: React.ReactNode }) => {
  const { data, setCanUseApp, setData, refetchConnectWallet, setError } =
    useUserContext();
  const { disconnect: disconnectWallet } = useDisconnect();
  const { isConnected, address } = useWalletConnection();
  const { isConnecting } = useAccount();
  const router = useRouter();
  const pathName = usePathname();

  const isWalletReady = typeof isConnected === 'boolean';
  const hasPlan = useMemo(() => {
    return data?.isActivePlan;
  }, [isWalletReady, data, data.isLoading]);

  const isActiveInMaxleads = useMemo(() => {
    const active = data?.isActiveInMaxleads ?? data?.isActiveInMaxLeads;
    if (active !== undefined) return Boolean(active);
    return false;
  }, [data]);

  const hasMaxleadsStatus =
    data?.isActiveInMaxleads !== undefined ||
    data?.isActiveInMaxLeads !== undefined;

  const isReady = useMemo(() => {
    return (
      isWalletReady &&
      data !== null &&
      data !== undefined &&
      hasPlan !== null &&
      hasPlan !== undefined &&
      hasMaxleadsStatus &&
      isConnected &&
      (data.isLoading === false || data.isLoading === null)
    );
  }, [isWalletReady, data, data.isLoading, hasPlan, hasMaxleadsStatus, isConnected]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token && pathName !== '/') {
      router.push('/');
    }
  }, [pathName, router]);

  useEffect(() => {
    if (!isWalletReady || isConnecting || data?.isLoading) return;

    if (isReady) {
      if (!isActiveInMaxleads) {
        setCanUseApp?.(false);
        setError?.(
          'Your account has been suspended by the administrator.\nPlease contact support to restore your access..'
        );
        if (pathName !== '/') {
          toast.error(
            'Your account has been suspended by the administrator.\nPlease contact support to restore your access..'
          );
          setData((prev: any) => ({ ...prev, isLoading: null }));
          router.push('/');
        }
      } else if (hasPlan) {
        setCanUseApp?.(true);
        setError?.(null);
      } else {
        setCanUseApp?.(false);
        if (pathName !== '/') {
          toast.error(
            'To access this panel, please make your initial investment on the 0xArbitrage platform.'
          );
          setData((prev: any) => ({ ...prev, isLoading: null }));
          router.push('/');
        }
      }
    } else if (!address && pathName !== '/') {
      router.push('/');
    }
  }, [
    isReady,
    hasPlan,
    isActiveInMaxleads,
    isWalletReady,
    address,
    disconnectWallet,
    pathName,
    setCanUseApp,
    setError,
    refetchConnectWallet,
    setData,
    router,
  ]);

  if (!isReady && pathName !== '/') {
    return (
      <div className="fixed inset-0 bg-black-200 flex flex-col items-center justify-center gap-3">
        <Spinner className="size-14" iconClassName="text-white" />
        <p className="text-white">
          Connecting to your wallet and loading your account details. Please
          wait...
        </p>
      </div>
    );
  }
  if (pathName !== '/' && (!hasPlan || !isActiveInMaxleads)) {
    return null;
  }
  return (hasPlan && isActiveInMaxleads) || pathName === '/' ? (
    <>{children}</>
  ) : null;
};

export default UserAuthorize;
